import { Link } from "react-router-dom";
import { ShieldCheck, Linkedin, MapPin } from "lucide-react";
import { SEO_PAGES } from "@/data/seoPages";

export const Footer = () => {
  return (
    <footer className="bg-ally-ink text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <span className="font-display font-black text-3xl tracking-tight">
              Defer<span className="text-ally-pink">Ally</span>
            </span>
            <p className="mt-4 text-white/60 max-w-sm leading-relaxed">
              The modern 1031 platform for qualified intermediaries, family offices, attorneys and wealth managers — every exchange, document and deadline in one place.
            </p>
            <p className="mt-3 text-white/40 max-w-sm text-sm leading-relaxed" data-testid="footer-scale-line">
              Built for the big and the small — from boutique shops to firms running 10,000+ exchanges a year.
            </p>
            <address className="mt-6 not-italic text-white/50 text-sm leading-relaxed space-y-1" data-testid="footer-office">
              <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0 text-ally-pink" /> 3423 Piedmont Rd NE, Atlanta, GA 30305</div>
              <div className="pl-[22px]"><a href="tel:+18442552559" data-testid="footer-phone" className="hover:text-ally-pink transition-colors">(844) 255-2559</a> · Mon–Fri, 8am–5pm EST</div>
            </address>
            <div className="mt-4 flex items-center gap-4 text-white/50 text-sm">
              <span>support@deferally.com</span>
              <a href="https://www.linkedin.com/company/deferally" target="_blank" rel="noopener noreferrer" aria-label="DeferAlly on LinkedIn" data-testid="footer-linkedin-link" className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-ally-pink hover:border-ally-pink transition-colors">
                <Linkedin size={16} />
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-display font-bold mb-4">Platform</h4>
            <ul className="space-y-3 text-white/60 text-sm">
              <li><Link to="/#pricing" className="hover:text-ally-pink transition-colors">Pricing</Link></li>
              <li><Link to="/pulse" className="hover:text-ally-pink transition-colors inline-flex items-center gap-1.5" data-testid="footer-pulse-link">Market Pulse {new Date() < new Date("2026-10-17") && <span className="text-[9px] font-black uppercase tracking-wider text-white bg-ally-pink rounded-full px-1.5 py-0.5 leading-none">New</span>}</Link></li>
              <li><Link to="/refer" className="hover:text-ally-pink transition-colors" data-testid="footer-refer-link">Refer &amp; earn $1,000</Link></li>
              <li><Link to="/?demo=1" className="hover:text-ally-pink transition-colors">Request a demo</Link></li>
              <li><Link to="/request-access" className="hover:text-ally-pink transition-colors" data-testid="footer-walkthrough-link">Watch the walkthrough</Link></li>
              <li><Link to="/enterprise/login" className="hover:text-ally-pink transition-colors">Enterprise login</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-4">Solutions</h4>
            <ul className="space-y-3 text-white/60 text-sm">
              {SEO_PAGES.filter((p) => p.kind !== "city").map((p) => (
                <li key={p.slug}><Link to={`/${p.slug}`} data-testid={`footer-solution-${p.slug}`} className="hover:text-ally-pink transition-colors">{p.nav}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-4">Company</h4>
            <ul className="space-y-3 text-white/60 text-sm">
              <li><Link to="/about" className="hover:text-ally-pink transition-colors">About</Link></li>
              <li><Link to="/insights" className="hover:text-ally-pink transition-colors">Insights</Link></li>
              <li><Link to="/contact" className="hover:text-ally-pink transition-colors">Contact</Link></li>
              <li><Link to="/rules" className="hover:text-ally-pink transition-colors">1031 Rules</Link></li>
              <li><Link to="/1031-exchange-companies" className="hover:text-ally-pink transition-colors" data-testid="footer-companies-link">Best 1031 Exchange Companies</Link></li>
              <li><Link to="/state-taxes" className="hover:text-ally-pink transition-colors">1031 Rules by State</Link></li>
              <li><Link to="/banks" className="hover:text-ally-pink transition-colors" data-testid="footer-banks-link">For banks</Link></li>
              <li><Link to="/trust" className="hover:text-ally-pink transition-colors inline-flex items-center gap-1.5" data-testid="footer-trust-link"><ShieldCheck size={13} className="text-ally-pink" /> Trust &amp; Security</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-white/40 text-sm">
          <div className="flex flex-col gap-2">
            <span>© {new Date().getFullYear()} DeferAlly Inc. All rights reserved.</span>
            <span data-testid="patent-pending" className="inline-flex items-center gap-2 self-start bg-white/5 border border-white/10 rounded-full px-3 py-1 text-white/50 text-xs font-semibold tracking-wide">
              <ShieldCheck size={13} className="text-ally-pink" />
              DeferAlly Enterprise™ — Patent Pending
            </span>
          </div>
          <div className="flex gap-6">
            <Link to="/terms" className="hover:text-white/70 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white/70 transition-colors">Privacy</Link>
            <Link to="/refund-policy" data-testid="footer-refund-policy" className="hover:text-white/70 transition-colors">Refund Policy</Link>
            <Link to="/sms-opt-in" data-testid="footer-sms-optin" className="hover:text-white/70 transition-colors">Text Alerts</Link>
          </div>
        </div>
        <div className="mt-8 text-center">
          <span data-testid="footer-atlanta" className="inline-flex items-center gap-1.5 text-white/30 text-xs tracking-wide">
            <MapPin size={12} className="text-ally-pink/70" /> Designed &amp; built in Atlanta, GA
          </span>
        </div>
      </div>
    </footer>
  );
};
